import React from 'react';
import { Hero } from '../components/Hero';
import { StatsSection } from '../components/StatsSection';
import { ProjectCard } from '../components/ProjectCard';
import { StoryCard } from '../components/StoryCard';
import { EmailSignup } from '../components/EmailSignup';
import { Button } from '../components/Button';
import { Droplets, Zap, BookOpen, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
export function Home() {
  const stats = [{
    value: '12+',
    label: 'Villages Served'
  }, {
    value: '8,000+',
    label: 'Lives Impacted'
  }, {
    value: '6',
    label: 'Wells Drilled'
  }, {
    value: '5+',
    label: 'Churches Supported'
  }, {
    value: '2',
    label: 'Countries Reached'
  }];
  const projects = [{
    title: 'Clean Water',
    description: 'Solar-powered wells bringing life and dignity to entire villages.',
    icon: <Droplets size={48} data-id="element-230" />,
    href: '/projects'
  }, {
    title: 'School Support',
    description: 'Solar power, books, and resources transforming education.',
    icon: <BookOpen size={48} data-id="element-231" />,
    href: '/projects'
  }, {
    title: 'Church Support',
    description: 'Solar systems enabling worship, teaching, and community impact.',
    icon: <Heart size={48} data-id="element-232" />,
    href: '/projects'
  }, {
    title: 'Medical Center',
    description: 'Solar-powered equipment saving lives in remote areas.',
    icon: <Zap size={48} data-id="element-233" />,
    href: '/projects'
  }];
  const stories = [{
    title: 'From Darkness to Light',
    excerpt: 'When solar power arrived at the village church, everything changed. Now they gather under the stars, singing praises with light that never dims.',
    image: 'https://cdn.magicpatterns.com/uploads/6S8xETngti9AGNCyQJh7qL/2025_09_BLF_era92Creative_05111_(1).jpg',
    category: 'Church',
    href: '/projects'
  }, {
    title: "A Grandmother's Walk for Water is Over",
    excerpt: 'For 40 years, she walked three miles each day. Now, clean water flows 100 feet from her home.',
    image: 'https://cdn.magicpatterns.com/uploads/epCr4jSniEEzY7ieCu8jj4/2025_08_BLF_era92Creative_05374.jpg',
    category: 'Water',
    href: '/projects'
  }, {
    title: 'Students Study After Dark',
    excerpt: 'Solar panels transformed a school. Now children stay late to learn, and test scores have doubled.',
    image: 'https://cdn.magicpatterns.com/uploads/gGSaTdSTfFpCUmDmPbQpJT/2025_09_BLF_era92Creative_05080.jpg',
    category: 'Education',
    href: '/projects'
  }];
  return <div className="pt-20" data-id="element-234">
      <Hero title="Love is Not Love Until You Give it Away." subtitle="Bringing clean water, sustainable power, and biblical discipleship to communities across the globe." backgroundImage="https://cdn.magicpatterns.com/uploads/73J2j4fYuXMc2mDuvxTWcS/2025_09_BLF_era92Creative_05045.jpg" buttons={[{
      label: 'SEE OUR PROJECTS',
      href: '/projects',
      variant: 'primary'
    }, {
      label: 'JOIN A TRIP',
      href: '/trips',
      variant: 'outline'
    }]} data-id="element-235" />

      {/* Video Section - New Africa-focused video */}
      <section className="py-20 bg-white" data-id="element-236">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-237">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-238">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-239">See the Impact</h2>
            <p className="text-lg text-[var(--color-text-light)] max-w-3xl mx-auto" data-id="element-240">
              Watch how your support transforms lives across Africa and beyond.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="aspect-video rounded-lg overflow-hidden shadow-2xl" data-id="element-241">
            <iframe width="100%" height="100%" src="https://www.youtube.com/embed/tgpMG3bDMZ0" title="Big Love Foundation Impact Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-242"></iframe>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <StatsSection stats={stats} data-id="element-243" />

      {/* Simplified Mission Statement */}
      <section className="py-16 bg-[var(--color-gray-light)]" data-id="element-244">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-245">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-246">
            <h2 className="text-[var(--color-text)] mb-6" data-id="element-247">Our Mission</h2>
            <p className="text-xl text-[var(--color-text-light)] leading-relaxed" data-id="element-248">
              We partner with communities to deliver clean water, sustainable
              power, and the Gospel—creating lasting transformation through
              dignity and faith.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Project Categories - Visual Focus */}
      <section className="py-20 bg-white" data-id="element-249">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-250">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-251">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-252">Our Projects</h2>
            <p className="text-lg text-[var(--color-text-light)] max-w-3xl mx-auto" data-id="element-253">
              Every project creates lasting impact through practical solutions
              and Kingdom purpose.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-254">
            {projects.map((project, index) => <ProjectCard key={index} {...project} delay={index * 0.1} data-id="element-255" />)}
          </div>

          <div className="text-center" data-id="element-256">
            <Button variant="primary" size="lg" href="/projects" data-id="element-257">
              EXPLORE ALL PROJECTS
            </Button>
          </div>
        </div>
      </section>

      {/* Stories - More Visual */}
      <section className="py-20 bg-[var(--color-gray-light)]" data-id="element-258">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-259">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-260">
            <h2 className="text-[var(--color-text)] mb-4" data-id="element-261">
              Real People. Real Change.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-262">
            {stories.map((story, index) => <StoryCard key={index} {...story} delay={index * 0.1} data-id="element-263" />)}
          </div>
        </div>
      </section>

      {/* News Link */}
      <section className="py-16 bg-white" data-id="element-264">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-265">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-266">
            <h3 className="text-2xl text-[var(--color-text)] mb-4" data-id="element-267">
              Big Love in the News
            </h3>
            <p className="text-lg text-[var(--color-text-light)] mb-6" data-id="element-268">
              Featured in national publications, podcasts, and media coverage.
            </p>
            <Button variant="outline" href="/news" data-id="element-269">
              SEE MEDIA COVERAGE
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Trips CTA */}
      <section className="py-20 bg-[var(--color-primary)] text-white" data-id="element-270">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-271">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-272">
            <h2 className="text-white mb-6" data-id="element-273">
              Join Us in Uganda & the Philippines
            </h2>
            <p className="text-lg md:text-xl text-white/95 mb-8 leading-relaxed" data-id="element-274">
              Experience life-changing mission work. Serve alongside local
              communities. See God move in powerful ways.
            </p>
            <Button variant="secondary" size="lg" href="/trips" data-id="element-275">
              EXPLORE TRIPS
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Email Signup */}
      <EmailSignup data-id="element-276" />
    </div>;
}